import React from 'react';

import type {
  DirectusPaymentMethod,
  DirectusShippingMethod,
} from '@/stores/cartStoreSlices/PaymentMethodsSlice';

import type { DirectusClientAddress } from './fields/definitions';

// Define the props for the SelectableOptionList
interface SelectableOptionListProps<T> {
  options: T[];
  selectedOptionId: string | number | null;
  onSelect: (opt: T) => void;
  title?: string;
  intro?: string;
  canUnselect?: boolean;
  type: 'shipping' | 'payment' | 'address'; // Specify types
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode; // Optional function to render custom cards
  idField?: keyof T; // Allow customization for which field to use as the unique identifier (e.g., 'id' or 'uuid')
}
type SelectableOptionType =
  | DirectusClientAddress
  | DirectusPaymentMethod
  | DirectusShippingMethod;

const SelectableOptionList = <T extends SelectableOptionType>({
  options,
  selectedOptionId,
  onSelect,
  title,
  intro,
  type,
  renderOption,
  canUnselect = false,
  idField = 'id',
}: SelectableOptionListProps<T>) => {
  return (
    <div>
      {title && <h2 className="mb-2 text-xl font-bold">{title}</h2>}
      {intro && (
        <div className="mb-2 font-neutrifi text-sm text-gray-500">{intro}</div>
      )}
      <div className="space-y-2">
        {options.map((option) => (
          <div
            key={option.id}
            className={`flex cursor-pointer items-center justify-between rounded-lg border-2 p-2 transition-colors duration-300 ${
              selectedOptionId === option[idField]
                ? 'border-secondary-600 bg-secondary-50'
                : 'border-gray-200 bg-white hover:bg-gray-100'
            }`}
            role="radio"
            aria-checked={selectedOptionId === option[idField]}
            onKeyDown={() => onSelect(option)}
            onClick={() => onSelect(option)}
            tabIndex={0}
          >
            <div className="flex w-full items-center">
              <input
                type="radio"
                id={option[idField] as string}
                name={type}
                value={option[idField] as string}
                checked={selectedOptionId === option[idField]}
                onChange={() => onSelect(option)}
                className="radio-secondary radio radio-sm mr-2 hidden" // Hide the native radio input
              />
              {renderOption &&
                renderOption(option, selectedOptionId === option[idField])}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SelectableOptionList;
